import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface TencentHunyuan3dRapidParams {
  /**
   * 文本提示词，可选参数（与image_base64、image_url三选一，且只能提供一个）。用于描述要生成的3D模型。长度不超过200个字符
   * @length 0 - 200
   */
  prompt?: string;
  /**
   * Base64编码的图片，可选参数（与prompt、image_url三选一，且只能提供一个）。图片格式支持jpg、png、jpeg、webp。图片尺寸在128x128到5000x5000像素之间
   */
  image_base64?: string;
  /**
   * 图片URL地址，可选参数（与prompt、image_base64三选一，且只能提供一个）。图片格式支持jpg、png、jpeg、webp。图片尺寸在128x128到5000x5000像素之间
   */
  image_url?: string;
  /**
   * 结果格式，可选参数。指定生成的3D模型文件格式
   * @values OBJ | GLB | STL | USDZ | FBX | MP4
   */
  result_format?: 'OBJ' | 'GLB' | 'STL' | 'USDZ' | 'FBX' | 'MP4';
  /**
   * 是否启用PBR材质，可选参数。启用后会生成更真实的材质效果
   */
  enable_pbr?: boolean;
}

export interface TencentHunyuan3dRapidResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * tencent_hunyuan_3d_rapid
 *
 * 腾讯云混元生3D极速版生成器
 * 
 * Model: hunyuan-3d-rapid
 * Features: 文本生成3D模型 (Text-to-3D) / 图像生成3D模型 (Image-to-3D)
 * Input: 文本描述或图片（三选一：prompt/image_base64/image_url）
 * Output Formats: OBJ, GLB, STL, USDZ, FBX, MP4
 * Image Requirements: 128x128至5000x5000像素，支持jpg/png/jpeg/webp
 * Prompt Length: 最多200字符
 * PBR Support: 支持PBR材质生成
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, tencentHunyuan3dRapid } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await tencentHunyuan3dRapid({
 *   // 参数见 TencentHunyuan3dRapidParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 TencentHunyuan3dRapidParams 接口定义
 * @returns 生成的资源列表
 */
export async function tencentHunyuan3dRapid(
  params: TencentHunyuan3dRapidParams,
): Promise<TencentHunyuan3dRapidResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 4000,
    maxAttempts: 90,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'tencent_hunyuan_3d_rapid',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: TencentHunyuan3dRapidResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
